import React from "react";
import { Navigate } from "react-router-dom";
import { useFeatureFlags } from "./useFeatureFlag";

interface FeatureGuardProps {
  /** A single flag, or several of which any one grants access. */
  feature: string | string[];
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * Renders children only if `feature` is enabled for the current (JWT-derived)
 * tenant; otherwise redirects. Use to block routes for tenants without a feature
 * (deep links / direct URLs), complementing the sidebar filtering. Waits for the
 * flags to load before deciding, so the redirect never fires on the loading render.
 * Passing several flags guards a page that hosts more than one feature — any one
 * of them being on is enough to reach it.
 */
export const FeatureGuard: React.FC<FeatureGuardProps> = ({
  feature,
  children,
  redirectTo = "/dashboard",
}) => {
  const { isEnabled, isLoading } = useFeatureFlags();
  if (isLoading) return null;
  const allowed = (Array.isArray(feature) ? feature : [feature]).some(isEnabled);
  return allowed ? <>{children}</> : <Navigate to={redirectTo} replace />;
};
